import Drawer from '@/common/components/Drawer/Drawer';
import { LelyRadioButton } from '@/common/components';
import { useLelyAuth } from '@/auth/useLelyAuth';
import { ALARM_ID_DECIMAL, ALARM_ID_HEXADECIMAL } from '@/common/constants';
import { setStoredRepresentation } from '@/components/App/hooks/useAlarmIdRepresentation';
import { useAppSelector } from '@/reducers/rootReducer';
import {
  setAlarmIdRepresentation,
  userDetailsSelectors,
} from '@/reducers/userDetailsSlice';
import {
  Box,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SettingsDrawerProps } from '../ManageSettings';

type AlarmIdRepresentation =
  | typeof ALARM_ID_DECIMAL
  | typeof ALARM_ID_HEXADECIMAL;

const alarmIdOptions: AlarmIdRepresentation[] = [
  ALARM_ID_DECIMAL,
  ALARM_ID_HEXADECIMAL,
];

const ManageAlarmIdDrawer = ({
  open,
  onClose,
  onCloseAll,
}: SettingsDrawerProps) => {
  const dispatch = useDispatch();
  const { user } = useLelyAuth();
  const alarmIdRepresentation = useAppSelector(
    userDetailsSelectors.selectAlarmIdRepresentation,
  );
  const [draftAlarmId, setDraftAlarmId] = useState<AlarmIdRepresentation>(
    alarmIdRepresentation,
  );

  useEffect(() => {
    if (open) setDraftAlarmId(alarmIdRepresentation);
  }, [open, alarmIdRepresentation]);

  const hasUnsavedChanges = draftAlarmId !== alarmIdRepresentation;

  const handleAlarmIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === ALARM_ID_DECIMAL || value === ALARM_ID_HEXADECIMAL) {
      setDraftAlarmId(value);
    }
  };

  const handleAlarmIdApply = () => {
    dispatch(setAlarmIdRepresentation(draftAlarmId));

    if (user?.email) {
      setStoredRepresentation(user.email, draftAlarmId);
    }

    onClose();
  };

  return (
    <Drawer
      title="Alarm ID"
      open={open}
      onApply={handleAlarmIdApply}
      onClose={onClose}
      onCloseAll={onCloseAll}
      showFooter={true}
      showBackButton={true}
      invisibleBackdrop
      hasUnsavedChanges={hasUnsavedChanges}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.primary">
          Select in which way the alarm ID’s are represented throughout the
          application.
        </Typography>
        <RadioGroup value={draftAlarmId} onChange={handleAlarmIdChange}>
          {alarmIdOptions.map((alarmId) => (
            <LelyRadioButton
              key={alarmId}
              value={alarmId}
              label={alarmId}
            />
          ))}
        </RadioGroup>
      </Box>
    </Drawer>
  );
};

export default ManageAlarmIdDrawer;
